import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type FilterTag } from 'src/components/shared/appliedFilterTags/appliedFilterTags';
import SitesCoLocationService from 'src/services/sites/sitesCoLocationService';
import SitesFilterService from 'src/services/sites/sitesFilterService';
import SitesService from 'src/services/sites/sitesService';
import { type Site, type SitePayload } from 'src/services/sites/types';
import { type Region } from 'src/services/tasks/types';
import { type RootState } from 'src/store/store';
import { getUpdatedFiltersOnTagRemoval } from 'src/utils/tableUtils';

import { INITIAL_FILTER_OPTIONS, INITIAL_STATE } from './constants';
import { type BuildingType, type Country, type FilterOptions, type Org, type SubOrg, type TableDataRow } from './types';

/**
 * get all sites
 * @returns {Object}
 */
export const getAllSites = createAsyncThunk('sites/getSites', async () => {
  const { data } = await SitesService.getSites();
  return data;
});

/**
 * get all countries
 * @returns {Object}
 */
export const getCountries = createAsyncThunk('sites/getCountries', async () => {
  const { data } = await SitesService.getCountries();
  return data;
});

/**
 * get all regions
 * @returns {Object}
 */
export const getRegions = createAsyncThunk('sites/getRegions', async () => {
  return await SitesService.getRegions();
});

/**
 * get all orgs
 * @returns {Object}
 */
export const getOrgs = createAsyncThunk('sites/getOrgs', async () => {
  const { data } = await SitesService.getOrgs();
  return data;
});

/**
 * get all subOrgs
 * @returns {Object}
 */
export const getSubOrgs = createAsyncThunk('sites/getSubOrgs', async () => {
  const { data } = await SitesService.getSubOrgs();
  return data;
});

/**
 * get all buildingTypes
 * @returns {Object}
 */
export const getBuildingTypes = createAsyncThunk('sites/getBuildingTypes', async () => {
  const { data } = await SitesService.getBuildingTypes();
  return data;
});

/**
 * convert a table row to a site object
 */

export const tableRowToSite = (
  tableRow: TableDataRow,
  countries: Country[],
  orgs: Org[],
  subOrgs: Org[],
  buildingTypes: BuildingType[],
  regions: Region[],
): SitePayload => {
  const site: SitePayload = {
    site: tableRow.site,
    countryId: countries.find((country: { name: string }) => country?.name === tableRow.country)?.id,
    organizationId: orgs.find((org: { name: string }) => org?.name === tableRow.org)?.id,
    subOrganizationId: subOrgs.find((subOrg: { name: string }) => subOrg?.name === tableRow.subOrg)?.id,
    buildingTypeId: buildingTypes.find(
      (buildingType: { label: string }) => buildingType?.label === tableRow.buildingType,
    )?.id,
    regionId: regions.find((region: { name: string }) => region?.name === tableRow.region)?.id,
    isActive: tableRow.isActive,
    description: tableRow.description,
    headcount: tableRow.headcount ? tableRow.headcount : 100, //Temp Value
    participatingInDecisionHubPilot: tableRow.participatingInDecisionHubPilot ?? false,
    atozNotificationsEnabled: tableRow.atozNotificationsEnabled ?? false,
    jobModificationsEnabled: tableRow.jobModificationsEnabled ?? false,
  } as SitePayload;
  site.id = tableRow.id ?? site.id;
  return site;
};

/**
 * convert site object to a table row
 */

export const siteToTableRow = (
  site: Site,
  countries: Country[],
  orgs: Org[],
  subOrgs: SubOrg[],
  buildingTypes: BuildingType[],
  regions: Region[],
  coLocatedSites: string,
): TableDataRow => {
  const tableRow: TableDataRow = {
    site: site?.site,
    country: site?.country?.name
      ? site?.country?.name
      : countries.find((country: { id: number }) => country?.id === site.countryId)?.name,
    org: site?.organization?.name
      ? site?.organization?.name
      : orgs.find((org: { id: number }) => org?.id === site.organizationId)?.name,
    subOrg: site?.subOrganization?.name
      ? site?.subOrganization?.name
      : subOrgs.find((subOrg: { id: number }) => subOrg?.id === site.subOrganizationId)?.name,
    buildingType: site?.buildingType?.label
      ? site?.buildingType?.label
      : buildingTypes.find((buildingType: { id: number }) => buildingType?.id === site.buildingTypeId)?.label,
    region: site?.region?.name
      ? site?.region?.name
      : regions.find((region: { id: number }) => region?.id === site.regionId)?.name,
    isActive: site?.isActive,
    description: site?.description ?? '',
    headcount: site?.headcount,
    tldHeadcount: site?.tldHeadcount,
    siteCoLocations: coLocatedSites,
    participatingInDecisionHubPilot: site?.participatingInDecisionHubPilot,
    atozNotificationsEnabled: site?.atozNotificationsEnabled,
    jobModificationsEnabled: site?.jobModificationsEnabled,
  } as TableDataRow;
  tableRow.id = site.id ?? tableRow.id;
  return tableRow;
};

export const addOrEditSite = createAsyncThunk('sites/createOrUpdateSite', async (site: TableDataRow, { getState }) => {
  const state = getState() as RootState;
  const payload = tableRowToSite(
    site,
    state.siteConfigPage.countries,
    state.siteConfigPage.orgs,
    state.siteConfigPage.subOrgs,
    state.siteConfigPage.buildingTypes,
    state.siteConfigPage.regions,
  );
  const res = await SitesService.createOrUpdateSite(payload, state.siteConfigPage.selectedSite);

  if (res) {
    payload.id = payload.id ?? res.data.id;
    return {
      ...siteToTableRow(
        payload as Site,
        state.siteConfigPage.countries,
        state.siteConfigPage.orgs,
        state.siteConfigPage.subOrgs,
        state.siteConfigPage.buildingTypes,
        state.siteConfigPage.regions,
        site.siteCoLocations,
      ),
    };
  }
});

/** Site Config Page Slice */
const { reducer, actions } = createSlice({
  name: 'siteConfigPageSlice',
  initialState: INITIAL_STATE,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSearchField: (state, action: PayloadAction<string>) => {
      state.searchField = action.payload;
    },
    setIsCreateModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isCreateModalOpen = action.payload;
    },
    setSelectedSite: (state, action: PayloadAction<TableDataRow | undefined>) => {
      state.selectedSite = action.payload;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setTotalPages: (state, action: PayloadAction<number>) => {
      state.totalPages = action.payload;
    },
    setPerPage: (state, action: PayloadAction<number>) => {
      state.perPage = action.payload;
    },
    setIsAdvancedFilterSheetOpen: (state, action: PayloadAction<boolean>) => {
      state.isAdvancedFilterSheetOpen = action.payload;
    },
    applyFilters: (state, action: PayloadAction<FilterOptions>) => {
      state.appliedFilterOptions = action.payload;
      state.filterTags = SitesFilterService.getFilterTags(action.payload);
      state.currentPage = 1;
    },
    resetFilters: (state) => {
      state.appliedFilterOptions = INITIAL_FILTER_OPTIONS;
      state.currentPage = 1;
    },
    removeTagAndUpdateFilters: (state, action: PayloadAction<FilterTag<FilterOptions>>) => {
      state.filterTags = state.filterTags.filter((tag) => tag.value !== action.payload.value);
      state.appliedFilterOptions = getUpdatedFiltersOnTagRemoval(
        state.appliedFilterOptions,
        action.payload.optionName,
        action.payload.value,
      );
      state.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllSites.fulfilled, (state, { payload }) => {
      state.siteCoLocationMap = SitesCoLocationService.generateSiteCoLocationMap(payload);
      state.tableData = payload.map((site: Site) => {
        const coLocatedSites = SitesCoLocationService.getCoLocatedSites(site, state.siteCoLocationMap);
        return siteToTableRow(
          site,
          state.countries,
          state.orgs,
          state.subOrgs,
          state.buildingTypes,
          state.regions,
          coLocatedSites,
        );
      });
      state.isLoading = false;
    });
    builder.addCase(getCountries.fulfilled, (state, { payload }) => {
      state.countries = payload;
    });
    builder.addCase(getRegions.fulfilled, (state, { payload }) => {
      state.regions = payload;
    });
    builder.addCase(getOrgs.fulfilled, (state, { payload }) => {
      state.orgs = payload;
    });
    builder.addCase(getSubOrgs.fulfilled, (state, { payload }) => {
      state.subOrgs = payload;
    });
    builder.addCase(getBuildingTypes.fulfilled, (state, { payload }) => {
      state.buildingTypes = [...payload].sort((a: BuildingType, b: BuildingType) => {
        return a.label.localeCompare(b.label);
      });
    });
    builder.addCase(addOrEditSite.fulfilled, (state, { payload }) => {
      if (payload) {
        state.searchField = payload.site;

        const isAlreadyInTable = state.tableData.some((row) => {
          return row.id === payload.id;
        });

        if (isAlreadyInTable) {
          // this will be the case when editing an existing site
          state.tableData = state.tableData.map((row) => (row.id === payload.id ? payload : row));
        } else {
          // this will be the case when adding a new site
          state.tableData = [...state.tableData, payload];
        }
      }
      state.isLoading = false;
    });
  },
});

export const {
  setIsLoading,
  setSearchField,
  setIsCreateModalOpen,
  setSelectedSite,
  setCurrentPage,
  setTotalPages,
  setPerPage,
  setIsAdvancedFilterSheetOpen,
  applyFilters,
  removeTagAndUpdateFilters,
  resetFilters,
} = actions;

export default reducer;
